import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/rating/rating.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import type SlRating from '@shoelace-style/shoelace/dist/components/rating/rating.js';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import axios, { AxiosError } from 'axios';
import { defaultDomain } from '../api/default-domain';
import { ApiFactory } from '../api/easypos-api';
import type { ITrackedSaleResponse } from '../api/easypos-api';
import { styles as sharedStyles } from '../styles/shared-styles';

// Phase Y.B — customer-facing order tracking page.
//
// URL: /track/:token
//
// Anonymous, no shell, no app navigation. The customer scanned a QR
// from their receipt and lands here. The page:
//
//   1. Fetches /api/track/{token} once for the initial render (status,
//      lines, age).
//   2. Negotiates a SignalR connection anonymously via track-token.
//      Server pushes "saleStatusChanged" / "saleClosed" to user-id
//      "track:{TOKEN}". Status changes update the UI within a tap of
//      the operator's "Mark ready" button.
//   3. On "saleClosed", closes the connection and renders the final
//      state.
//
// Falls back to slow polling (every 15s) ONLY when the SignalR
// negotiate refuses with a tier-cap-reached or tracker-disabled
// reason. Polling is the graceful-degradation path, never the
// primary path.
const POLL_FALLBACK_MS = 15_000;
const HUB_METHOD = 'event';

@customElement('order-track-page')
export class OrderTrackPage extends LitElement {
  @state() private _sale: ITrackedSaleResponse | null = null;
  @state() private _loading = true;
  @state() private _notFound = false;
  @state() private _connectionMode: 'live' | 'polling' | 'connecting' =
    'connecting';
  // Wall-clock tick — bumps every 10s so the age display stays live
  // without burning a re-render per second.
  @state() private _now = Date.now();

  // Y.C2 — customer's chosen rating (0 = not yet rated). Visible from
  // any non-terminal state so the customer can pick a rating while
  // waiting; the "I have my order" button submits it (the button is
  // only enabled once the operator marks the order ready).
  @state() private _starRating = 0;
  @state() private _confirming = false;
  @state() private _confirmError: string | null = null;

  private _token = '';
  private _tickTimer?: number;
  private _pollTimer?: number;
  private _signalr: HubConnection | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--app-bg);
        color: var(--app-text);
      }
      .wrap {
        max-width: 480px;
        margin: 0 auto;
        padding: 32px 20px 64px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
      .shop-name {
        font-family: var(
          --font-mono,
          ui-monospace,
          SFMono-Regular,
          Menlo,
          monospace
        );
        font-size: 0.78rem;
        font-weight: 600;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
      .order-number {
        font-family: var(
          --font-mono,
          ui-monospace,
          SFMono-Regular,
          Menlo,
          monospace
        );
        font-weight: 700;
        font-size: clamp(3rem, 14vw, 4.5rem);
        line-height: 1;
        letter-spacing: -0.04em;
        color: var(--app-text);
        font-variant-numeric: tabular-nums;
      }
      .order-number .hash {
        opacity: 0.4;
        margin-right: 2px;
      }
      .status-block {
        padding: 22px 20px;
        border-radius: var(--sl-border-radius-medium);
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .status-line {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }
      .status-headline {
        font-family: var(--font-display, sans-serif);
        font-weight: 700;
        font-size: 1.5rem;
        letter-spacing: -0.01em;
      }
      .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        border-radius: 999px;
        font-family: var(--font-mono);
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .status-pill.paid {
        background: var(--sl-color-neutral-200);
        color: var(--sl-color-neutral-900);
      }
      .status-pill.preparing {
        background: var(--sl-color-warning-100);
        color: var(--sl-color-warning-900);
      }
      .status-pill.ready {
        background: var(--sl-color-success-100);
        color: var(--sl-color-success-900);
      }
      .status-pill.completed {
        background: var(--sl-color-neutral-200);
        color: var(--sl-color-neutral-700);
      }
      .status-pill.cancelled {
        background: var(--sl-color-danger-100);
        color: var(--sl-color-danger-900);
      }
      .sl-theme-dark .status-pill.paid,
      .sl-theme-dark .status-pill.completed {
        background: var(--sl-color-neutral-700);
        color: var(--sl-color-neutral-100);
      }
      .age {
        font-family: var(--font-mono);
        font-size: 0.85rem;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-variant-numeric: tabular-nums;
      }
      .lines {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .lines h3 {
        margin: 8px 0 0;
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 0.85rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--app-text-muted);
      }
      .line {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 12px;
        padding: 10px 4px;
        border-bottom: 1px solid var(--app-border);
        align-items: baseline;
      }
      .line:last-child {
        border-bottom: none;
      }
      .line .qty {
        font-family: var(--font-mono);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--app-text-muted);
      }
      .line .name {
        font-weight: 600;
      }
      .line .opts {
        margin-top: 4px;
        font-size: 0.85rem;
        color: var(--app-text-muted);
      }
      .stale {
        text-align: center;
        padding: 32px 16px;
        color: var(--app-text-muted);
      }
      .stale h2 {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1.2rem;
      }
      .footer {
        text-align: center;
        font-size: 0.78rem;
        color: var(--app-text-muted);
        margin-top: 24px;
      }
      .footer .dot {
        display: inline-block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: middle;
      }
      .footer .dot.live {
        background: var(--sl-color-success-500);
      }
      .footer .dot.polling {
        background: var(--sl-color-warning-500);
      }
      .footer .dot.connecting {
        background: var(--sl-color-neutral-400);
      }
      .loading {
        text-align: center;
        padding: 48px 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
      }
      /* Y.C2 — customer action card. Holds the star rating row and
         the single "I have my order" CTA. Visible while the sale
         isn't terminal; the CTA disables itself until the operator
         marks the order ready. */
      .action-card {
        padding: 20px 18px;
        border-radius: var(--sl-border-radius-medium);
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        display: flex;
        flex-direction: column;
        gap: 14px;
        align-items: stretch;
      }
      .rate-row {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
      }
      .rate-row .rate-label {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
      sl-rating {
        font-size: clamp(1.5rem, 6vw, 2rem);
      }
      .confirm-cta {
        --sl-button-font-size-large: 1.05rem;
      }
      .confirm-hint {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        text-align: center;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    const m = window.location.pathname.match(/\/track\/([^/?#]+)/);
    this._token = m ? decodeURIComponent(m[1]) : '';
    // 10s wall-clock tick — keeps the "5 min waiting" age display live
    // without re-rendering per second.
    this._tickTimer = window.setInterval(() => {
      this._now = Date.now();
    }, 10_000);
    // Kick off the read + SignalR negotiate together.
    void this._bootstrap();
  }

  disconnectedCallback(): void {
    if (this._tickTimer !== undefined) clearInterval(this._tickTimer);
    this._stopPolling();
    void this._signalr?.stop();
    this._signalr = null;
    super.disconnectedCallback();
  }

  // Initial fetch + realtime negotiate. Both run in parallel — the
  // page renders the initial state from the GET while SignalR is
  // still negotiating. On any failure path we degrade gracefully:
  // negotiate refused → polling. Initial GET 404 → "Order not found".
  private async _bootstrap() {
    if (!this._token) {
      this._notFound = true;
      this._loading = false;
      return;
    }
    await this._refresh();
    if (this._notFound || !this._sale) return;
    if (this._sale.isClosed) {
      // Already terminal — no point opening a realtime connection.
      this._connectionMode = 'live';
      return;
    }
    await this._connectSignalR();
  }

  private async _connectSignalR() {
    this._connectionMode = 'connecting';
    let negotiate: { url: string; accessToken: string };
    try {
      const base = defaultDomain ?? window.location.origin;
      const resp = await axios.post(
        `${base}/api/realtime/negotiate`,
        { trackToken: this._token },
        {
          headers: { 'Content-Type': 'application/json' },
          validateStatus: () => true,
        }
      );
      if (resp.status === 409) {
        // sale_closed — render whatever we already have and stop here.
        this._connectionMode = 'live';
        return;
      }
      if (resp.status === 403 || resp.status === 503) {
        // Tier-cap reached or realtime disabled on this environment.
        // Fall back to polling.
        this._startPolling();
        return;
      }
      if (resp.status !== 200 || !resp.data?.url) {
        // Unexpected. Polling is the safer fallback than leaving the
        // page on the initial render forever.
        this._startPolling();
        return;
      }
      negotiate = { url: resp.data.url, accessToken: resp.data.accessToken };
    } catch (err) {
      if (axios.isAxiosError(err as AxiosError)) {
        this._startPolling();
        return;
      }
      this._startPolling();
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(negotiate.url, {
        accessTokenFactory: () => negotiate.accessToken,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    // Single hub method — discriminator on the first arg, same shape
    // as the staff-side realtime channel.
    connection.on(HUB_METHOD, (eventName: string, payload: unknown) => {
      this._onRealtimeEvent(eventName, payload);
    });

    connection.onclose(() => {
      // If we close before terminal, the SignalR auto-reconnect
      // already kicked in. If reconnect ultimately fails the close
      // fires again with no further retries — degrade to polling.
      if (this._sale?.isClosed) return;
      this._startPolling();
    });

    try {
      await connection.start();
      this._signalr = connection;
      this._connectionMode = 'live';
    } catch {
      this._startPolling();
    }
  }

  private _onRealtimeEvent(eventName: string, payload: unknown) {
    if (!this._sale) return;
    if (eventName !== 'saleStatusChanged' && eventName !== 'saleClosed') return;
    const data = payload as {
      status?: string;
      ageSeconds?: number;
      isClosed?: boolean;
    };
    if (!data?.status) return;
    // Mutate in place by re-projecting the existing object. The page
    // already has lines + shop name + token from the initial GET —
    // status events only carry the deltas.
    this._sale = {
      ...this._sale,
      status: data.status as ITrackedSaleResponse['status'],
      ageSeconds:
        typeof data.ageSeconds === 'number'
          ? data.ageSeconds
          : this._sale.ageSeconds,
      isClosed: !!data.isClosed || eventName === 'saleClosed',
    };
    if (this._sale.isClosed) {
      // No further events expected; close the SignalR connection
      // proactively rather than waiting for the customer to leave the
      // page. Frees the server-side slot for the next active order.
      void this._signalr?.stop();
      this._signalr = null;
      this._stopPolling();
    }
  }

  private _startPolling() {
    if (this._pollTimer !== undefined) return;
    this._connectionMode = 'polling';
    this._pollTimer = window.setInterval(
      () => void this._refresh(),
      POLL_FALLBACK_MS
    );
  }

  private _stopPolling() {
    if (this._pollTimer !== undefined) {
      clearInterval(this._pollTimer);
      this._pollTimer = undefined;
    }
  }

  private async _refresh() {
    if (!this._token) {
      this._notFound = true;
      this._loading = false;
      return;
    }
    const r = await ApiFactory.anonymous().getTrackedSale(this._token);
    if (!r.success || !r.data) {
      this._notFound = true;
      this._loading = false;
      this._stopPolling();
      void this._signalr?.stop();
      this._signalr = null;
      return;
    }
    this._sale = r.data;
    this._loading = false;
    this._notFound = false;
    if (r.data.isClosed) {
      this._stopPolling();
      void this._signalr?.stop();
      this._signalr = null;
    }
  }

  private _ageLabel(): string {
    if (!this._sale) return '';
    const paidMs = new Date(this._sale.paidUtc).getTime();
    if (!Number.isFinite(paidMs)) return '';
    const seconds = Math.max(0, Math.floor((this._now - paidMs) / 1000));
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min waiting`;
    const hours = Math.floor(minutes / 60);
    const remMin = minutes % 60;
    if (hours < 24) return `${hours}h ${remMin}m waiting`;
    return new Date(this._sale.paidUtc).toLocaleString();
  }

  private _statusHeadline(status: string): string {
    switch (status) {
      case 'paid':
        return 'In the queue';
      case 'preparing':
        return 'Being prepared';
      case 'ready':
        return 'Ready for pickup';
      case 'completed':
        return 'All done — enjoy';
      case 'cancelled':
        return 'Order cancelled';
      default:
        return 'Order';
    }
  }

  // Y.C2 — customer taps "I have my order". Disables on terminal
  // status; submits the optional rating + transitions the sale to
  // Completed via the public confirm endpoint. On success the next
  // server response carries isClosed=true and the page re-renders
  // into the "All done — enjoy" terminal state automatically.
  private async _onConfirm() {
    if (!this._sale || this._sale.isClosed || this._confirming) return;
    if (this._sale.status !== 'ready') return;
    this._confirming = true;
    this._confirmError = null;
    const rating = this._starRating > 0 ? this._starRating : undefined;
    const r = await ApiFactory.anonymous().confirmTrackedSale(
      this._token,
      rating
    );
    this._confirming = false;
    if (!r.success || !r.data) {
      this._confirmError = r.error ?? 'Could not confirm. Try again.';
      return;
    }
    // Server returned the closed sale — adopt it directly. Stop the
    // realtime / polling loops; we're done.
    this._sale = r.data;
    void this._signalr?.stop();
    this._signalr = null;
    this._stopPolling();
  }

  private _onRateChange(e: Event) {
    const el = e.target as SlRating;
    this._starRating = Math.max(0, Math.min(5, Math.round(el.value)));
  }

  private _connectionLabel(): string {
    if (this._sale?.isClosed) return 'Refresh to look up a new order.';
    switch (this._connectionMode) {
      case 'live':
        return 'Live updates connected.';
      case 'polling':
        return 'Live updates unavailable — refreshing periodically.';
      case 'connecting':
        return 'Connecting to live updates…';
    }
  }

  render() {
    if (this._loading) {
      return html`
        <div class="wrap loading">
          <sl-spinner style="font-size: 2.5rem;"></sl-spinner>
          <p>Looking up your order…</p>
        </div>
      `;
    }
    if (this._notFound || !this._sale) {
      return html`
        <div class="wrap stale">
          <sl-icon
            name="question-circle"
            style="font-size: 3rem; opacity: 0.4;"
          ></sl-icon>
          <h2>Order not found</h2>
          <p>
            This link doesn't match an active order. The order may have been
            completed a while ago, or the QR code might be from a different
            shop.
          </p>
        </div>
      `;
    }
    const s = this._sale;
    return html`
      <div class="wrap">
        <div>
          <div class="shop-name">${s.shopDisplayName}</div>
          ${
            s.daySequence > 0
              ? html`
                  <div class="order-number">
                    <span class="hash">#</span>${s.daySequence}
                  </div>
                `
              : nothing
          }
        </div>

        <div class="status-block">
          <div class="status-line">
            <span class="status-pill ${s.status}">${s.status}</span>
            <span class="age">${this._ageLabel()}</span>
          </div>
          <div class="status-headline">${this._statusHeadline(s.status)}</div>
        </div>

        ${
          !s.isClosed
            ? html`
                <div class="action-card">
                  <div class="rate-row">
                    <div class="rate-label">Rate your service (optional)</div>
                    <sl-rating
                      .value=${this._starRating}
                      @sl-change=${(e: Event) => this._onRateChange(e)}
                      ?disabled=${this._confirming}
                    ></sl-rating>
                  </div>
                  <sl-button
                    class="confirm-cta"
                    variant="primary"
                    size="large"
                    ?loading=${this._confirming}
                    ?disabled=${this._confirming || s.status !== 'ready'}
                    @click=${() => this._onConfirm()}
                  >
                    I have my order
                  </sl-button>
                  <div class="confirm-hint">
                    ${
                      s.status === 'ready'
                        ? this._starRating > 0
                          ? 'Tap to confirm pickup and submit your rating.'
                          : 'Tap once you have collected your order.'
                        : 'Available once the shop marks your order ready.'
                    }
                  </div>
                  ${
                    this._confirmError
                      ? html`<sl-alert variant="danger" open
                          >${this._confirmError}</sl-alert
                        >`
                      : nothing
                  }
                </div>
              `
            : nothing
        }
        ${
          s.lines.length > 0
            ? html`
                <div class="lines">
                  <h3>Your order</h3>
                  ${s.lines.map(
                    (l) => html`
                      <div class="line">
                        <div class="qty">${l.quantity}×</div>
                        <div>
                          <div class="name">${l.name}</div>
                          ${
                            l.options.length > 0
                              ? html`<div class="opts">
                                  ${l.options.join(' · ')}
                                </div>`
                              : nothing
                          }
                        </div>
                      </div>
                    `
                  )}
                </div>
              `
            : nothing
        }

        <div class="footer">
          <span class="dot ${this._connectionMode}"></span>
          ${this._connectionLabel()}
        </div>
      </div>
    `;
  }
}
